import { useState, useEffect } from 'react';
import { Box, Button, Card, Typography, Select, MenuItem, TextField, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

const formatCitationHtml = (citation, bibliography) => {
  if (!citation) return '';

  let ref = {};
  if (citation?._id && Array.isArray(bibliography)) {
    ref = bibliography.find((r) => r?.id === citation._id) || {};
  }
  ref = { ...ref, ...(citation || {}) };

  // Safely escape HTML to prevent XSS
  const escapeHtml = (str) => {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  return (
    '<span>' +
    (ref.author ? '<span>' + escapeHtml(ref.author) + '. </span>' : '') +
    (ref.subtitle ? '<span>' + escapeHtml(ref.subtitle) + '. </span>' : '') +
    (ref.title ? '<i>' + escapeHtml(ref.title) + '. </i>' : '') +
    (ref.publisher ? '<span>' + escapeHtml(ref.publisher) + ', </span>' : '') +
    (ref.when ? '<span>' + escapeHtml(ref.when) + ' </span>' : '') +
    (ref.pages ? '<span>pp: ' + escapeHtml(ref.pages) + ' </span>' : '') +
    (ref.url ? "<span><a href='" + escapeHtml(ref.url) + "'>" + escapeHtml(ref.url) + '</a> </span>' : '') +
    (ref.timestamp ? '<span>Timestamp: ' + escapeHtml(ref.timestamp) + '</span>' : '') +
    '</span>'
  );
};

export const getReferencesHtml = (references, bibliography) => {
  let refs = '';
  try {
    if (!Array.isArray(references) || references.length === 0) {
      return refs;
    }

    // Safely clone and filter out invalid references (only remove null/undefined references)
    let _newrefs = JSON.parse(JSON.stringify(references || [])).filter(
      ref => ref && typeof ref === 'object'
    );

    // Sort with null checks
    _newrefs.sort((a, b) => {
      const nameA = a?.name?.toString() || '';
      const nameB = b?.name?.toString() || '';
      return nameA.localeCompare(nameB);
    });

    // Group references by name (including empty names)
    const groupedReferences = _newrefs.reduce((acc, ref) => {
      // Use empty string for null/undefined names
      const name = ref?.name?.toString() || '';
      if (!acc[name]) acc[name] = [];
      acc[name].push(ref);
      return acc;
    }, {});

    // Build HTML string
    for (const [name, citations] of Object.entries(groupedReferences)) {
      if (!Array.isArray(citations)) continue;  
      if (name) {
        refs += `<b>${name.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</b><br/>`;
      }
      citations.forEach(citation => {
        if (citation && typeof citation === 'object') {
          refs += formatCitationHtml(citation, bibliography) + '<br/>';
        }
      });
    }
  } catch (error) {
    console.error('Error generating references HTML:', error);
    return '';
  }
  
  return refs;
};

export function NoteReferences({ references, referencesId, bibliography, updateReferences, connectionIndex, noteIndex }) {
  const [refs, setRefs] = useState([]);
  const [isEditingReference, setIsEditingReference] = useState(false);
  const blankReference = { name: '', _id: '', pages: '', timestamp: '' };
  const [editReference, setEditReference] = useState(blankReference);
  const [editReferenceIndex, setEditReferenceIndex] = useState();

  useEffect(() => {
    try {
      if (!Array.isArray(references)) {
        setRefs([]);
        return;
      }
      
      // Safely clone and filter references
      const _refs = JSON.parse(JSON.stringify(references))
        .filter(ref => ref && typeof ref === 'object')
        .sort((a, b) => {
          const nameA = a?.name?.toString() || '';
          const nameB = b?.name?.toString() || '';
          return nameA.localeCompare(nameB);
        });
      
      setRefs(_refs);
    } catch (error) {
      console.error('Error processing references:', error);
      setRefs([]);
    }
  }, [references]);

  const formatCitation = (citation) => {
    let ref = {};
    if (citation._id && bibliography) {
      ref = bibliography.find((r) => r.id === citation._id);
    }
    ref = { ...ref, ...citation };
    return (
      <span>
        {ref.name && (
          <span>
            <b>{ref.name}</b> <br />
          </span>
        )}
        {ref.author && <span>{ref.author}. </span>}
        {ref.subtitle && <span>{ref.subtitle}. </span>}
        {ref.title && <i>{ref.title}. </i>}
        {ref.publisher && <span>{ref.publisher}, </span>}
        {ref.when && <span>{ref.when} </span>}
        {ref.pages && <span>pp: {ref.pages} </span>}
        {ref.url && (
          <span>
            <a href={ref.url} target="_blank" rel="noreferrer">
              {ref.url}
            </a>{' '}
          </span>
        )}
        {ref.timestamp && <span>Timestamp: {ref.timestamp}</span>}
      </span>
    );
  };

  const handleSetEditReference = (citation, index) => {
    let ref = { ...blankReference, ...citation };
    setEditReference(ref);
    setEditReferenceIndex(index);
    setIsEditingReference(true);
  };

  const handleSave = () => {
    // Create a new reference with all existing fields from editReference
    const _newref = { ...editReference };
    
    // Ensure _id is preserved (in case it was cleared)
    if (!_newref._id) _newref._id = '';
    
    // Update the references array
    let _newrefs = [...refs]; // Create a new array to ensure React detects the change
    if (editReferenceIndex !== undefined) {
      _newrefs[editReferenceIndex] = _newref;
    } else {
      _newrefs.push(_newref);
    }
    
    updateReferences({ 
      id: referencesId, 
      newrefs: _newrefs,
      path: `connections/${connectionIndex}/notes/${noteIndex}/references`
    });
    setRefs(_newrefs);
    setEditReference(blankReference);
    setIsEditingReference(false);
  };

  const handleDelete = (index) => {
    let _newrefs = refs;
    _newrefs.splice(index, 1);
    updateReferences({ 
      id: referencesId, 
      newrefs: _newrefs,
      path: `connections/${connectionIndex}/notes/${noteIndex}/references`
    });
  };

  const handleCancel = () => {
    setEditReference(blankReference);
    setIsEditingReference(false);
  };

  const updateReferenceSelection = (value) => {
    let ref = { ...editReference, _id: value };
    if (bibliography) {
      let _ref = bibliography.find((r) => r.id === value);
      if (_ref.name) ref.name = _ref.name;
      if (_ref.pages) ref.pages = _ref.pages;
      if (_ref.timestamp) ref.timestamp = _ref.timestamp;
    }
    setEditReference(ref);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">References</Typography>
        <IconButton 
          color="primary" 
          onClick={() => setIsEditingReference(true)}
          size="small"
        >
          <AddIcon />
        </IconButton>
      </Box>
      {refs &&
        refs.map((citation, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
            <Typography sx={{ flex: 1, fontSize: '0.875rem' }}>{formatCitation(citation)}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton 
                size="small" 
                onClick={() => handleSetEditReference(citation, i)}
                sx={{ p: 0.5 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                color="error" 
                onClick={() => handleDelete(i)}
                sx={{ p: 0.5 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ))}
      {isEditingReference && (
        <Card sx={{ p: 1.5, mt: 1 }}>
          <Typography variant="subtitle1" gutterBottom>Add Reference</Typography>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" gutterBottom>Name:</Typography>
            <TextField
              value={editReference.name}
              onChange={(e) => setEditReference({ ...editReference, name: e.target.value })}
              fullWidth
              size="small"
              margin="dense"
            />
          </Box>
          {bibliography ? (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="body2" gutterBottom>_id:</Typography>
              <Select 
                value={editReference._id} 
                onChange={(e) => updateReferenceSelection(e.target.value)} 
                fullWidth 
                size="small"
                margin="dense"
              >
                <MenuItem value="">Select a bibliography item</MenuItem>
                {bibliography.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    ({r.type && r.type.toUpperCase()[0]}) {r.author} - {r.title} - {r.publisher} - {r.when} ({r.id})
                  </MenuItem>
                ))}
              </Select>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">No bibliography available to select from!</Typography>
          )}
          <Box sx={{ mb: 1.5, display: 'flex', gap: 1 }}>
            <Box>
            <Typography variant="body2" gutterBottom>Pages:</Typography>
            <TextField
              value={editReference.pages}
              onChange={(e) => setEditReference({ ...editReference, pages: e.target.value })}
              fullWidth
              size="small"
              margin="dense"
            />
            </Box>
            <Box>
            <Typography variant="body2" gutterBottom>Timestamp:</Typography>
            <TextField
              value={editReference.timestamp}
              onChange={(e) => setEditReference({ ...editReference, timestamp: e.target.value })}
              fullWidth
              size="small"
              margin="dense"
            />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              onClick={handleSave} 
              color="primary" 
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
            >
              Save
            </Button>
            <Button 
              onClick={handleCancel} 
              color="secondary" 
              variant="outlined"
              size="small"
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </Box>
        </Card>
      )}
    </Box>
  );
}
